/**
 * Google Analytics 4 (GA4) utility functions
 * 
 * Usage:
 * - Set REACT_APP_GA4_MEASUREMENT_ID in your .env file
 * - Import and use trackEvent, trackPageView, etc.
 */

declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
    scrollDepthTracked?: Record<number, boolean>;
    videoProgressTracked?: Record<string, boolean>;
  }
}

// Get GA4 Measurement ID from environment variable
// Note: The HTML tag in index.html uses 'G-DY4H1FFP9G' hardcoded
// This env var is used as fallback if HTML tag is not present
const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-DY4H1FFP9G';

/**
 * Initialize Google Analytics 4
 * Call this once when the app loads
 * 
 * Note: If the Google tag is already loaded from index.html, this function
 * will detect it and just ensure proper configuration for SPA routing.
 */
export function initializeGA4(): void {
  // Check if GA4 is already loaded from HTML tag (in index.html)
  const existingGtagScript = document.querySelector(
    'script[src*="googletagmanager.com/gtag/js"]'
  );
  
  // If HTML tag already loaded GA, use the hardcoded ID from index.html
  const measurementId = existingGtagScript 
    ? 'G-DY4H1FFP9G' 
    : GA4_MEASUREMENT_ID;

  if (!measurementId) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "GA4 Measurement ID not found. Set REACT_APP_GA4_MEASUREMENT_ID in .env file"
      );
    }
    return;
  }

  // If gtag is already available (from HTML tag), just configure for SPA
  if (existingGtagScript && window.gtag && window.dataLayer) {
    // HTML tag already loaded, configure for SPA routing
    // Send initial page view manually, then disable auto page views for route changes
    window.gtag("config", measurementId, {
      send_page_view: false, // We'll track page views manually for SPA
    });
    // Send initial page view to ensure data collection is active
    trackPageView(window.location.pathname + window.location.search, document.title);
    return;
  }

  // Fallback: Initialize dataLayer if not already present
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  
  // Only set up gtag function if it doesn't exist
  if (!window.gtag) {
    window.gtag = function () {
      if (window.dataLayer) {
        window.dataLayer.push(arguments);
      }
    };
  }

  // Configure gtag
  window.gtag("js", new Date().toISOString());
  window.gtag("config", measurementId, {
    send_page_view: false, // We'll track page views manually for SPA
  });

  // Only load the script if HTML tag didn't already load it
  if (!existingGtagScript) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  }
}

/**
 * Track a page view
 * Call this when navigating to a new page in your SPA
 */
export function trackPageView(path: string, title?: string): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) {
    return;
  }

  window.gtag("config", GA4_MEASUREMENT_ID, {
    page_path: path,
    page_title: title || document.title,
  });
}

/**
 * Track a custom event
 * 
 * @param eventName - Name of the event (e.g., 'gallery_view', 'image_click')
 * @param eventParams - Additional parameters for the event
 * 
 * @example
 * trackEvent('gallery_view', {
 *   gallery_id: '123',
 *   gallery_title: 'Wedding Photography'
 * });
 */
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, any>
): void {
  if (!window.gtag) {
    return;
  }

  window.gtag("event", eventName, eventParams);
}

/**
 * Track when a user views a gallery
 */
export function trackGalleryView(galleryId: string, galleryTitle: string): void {
  trackEvent("gallery_view", {
    gallery_id: galleryId,
    gallery_title: galleryTitle,
  });
}

/**
 * Track when a user clicks on an image
 */
export function trackImageClick(
  imageId: string,
  galleryId?: string,
  imageIndex?: number
): void {
  trackEvent("image_click", {
    image_id: imageId,
    gallery_id: galleryId,
    image_index: imageIndex,
  });
}

/**
 * Track when a user submits the contact form
 */
export function trackContactFormSubmit(success: boolean): void {
  trackEvent("contact_form_submit", {
    success: success,
  });
}

/**
 * Track when a user views the lightbox
 */
export function trackLightboxView(imageId: string, galleryId?: string): void {
  trackEvent("lightbox_view", {
    image_id: imageId,
    gallery_id: galleryId,
  });
}

/**
 * Track when a user clicks a CTA button
 */
export function trackCTAClick(ctaName: string, location: string): void {
  trackEvent("cta_click", {
    cta_name: ctaName,
    location: location,
  });
}

/**
 * Set user properties (user ID, etc.)
 */
export function setUserProperties(properties: Record<string, any>): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) {
    return;
  }

  window.gtag("set", "user_properties", properties);
}

/**
 * Set user ID for cross-device tracking
 */
export function setUserId(userId: string): void {
  const measurementId = GA4_MEASUREMENT_ID || 'G-DY4H1FFP9G';
  if (!window.gtag) {
    return;
  }

  window.gtag("config", measurementId, {
    user_id: userId,
  });
}

/**
 * Track scroll depth (25%, 50%, 75%, 90%)
 * Call this from a scroll event handler
 */
export function trackScrollDepth(scrollPercent: number): void {
  // Only track at specific milestones to avoid spam
  const milestones = [25, 50, 75, 90];
  const milestone = milestones.find(m => scrollPercent >= m && scrollPercent < m + 5);
  
  if (milestone && !window.scrollDepthTracked) {
    window.scrollDepthTracked = window.scrollDepthTracked || {};
    if (!window.scrollDepthTracked[milestone]) {
      window.scrollDepthTracked[milestone] = true;
      trackEvent("scroll", {
        scroll_depth: milestone,
        page_path: window.location.pathname,
      });
    }
  }
}

/**
 * Track outbound/external link clicks
 */
export function trackOutboundLink(url: string, linkText?: string): void {
  trackEvent("click", {
    link_url: url,
    link_text: linkText || "",
    link_type: "external",
    page_path: window.location.pathname,
  });
}

/**
 * Track social media link clicks
 */
export function trackSocialClick(platform: string, url: string): void {
  trackEvent("social_click", {
    social_network: platform,
    social_action: "click",
    social_target: url,
    page_path: window.location.pathname,
  });
}

/**
 * Track file downloads
 */
export function trackFileDownload(fileName: string, fileType?: string): void {
  trackEvent("file_download", {
    file_name: fileName,
    file_type: fileType || fileName.split('.').pop() || "unknown",
    page_path: window.location.pathname,
  });
}

/**
 * Track form field interactions (focus, blur)
 */
export function trackFormFieldInteraction(
  formName: string,
  fieldName: string,
  action: "focus" | "blur" | "change"
): void {
  trackEvent("form_field_interaction", {
    form_name: formName,
    field_name: fieldName,
    interaction_type: action,
    page_path: window.location.pathname,
  });
}

/**
 * Track navigation menu clicks
 */
export function trackNavigationClick(linkText: string, linkPath: string): void {
  trackEvent("navigation_click", {
    link_text: linkText,
    link_path: linkPath,
    page_path: window.location.pathname,
  });
}

/**
 * Track 404 errors
 */
export function track404Error(path: string): void {
  trackEvent("exception", {
    description: `404: ${path}`,
    fatal: false,
    page_path: path,
  });
}

/**
 * Track engagement time (time spent on page)
 */
export function trackEngagementTime(seconds: number): void {
  trackEvent("engagement_time", {
    engagement_time_msec: seconds * 1000,
    page_path: window.location.pathname,
  });
}

/**
 * Track video play (if videos are added later)
 */
export function trackVideoPlay(
  videoTitle: string,
  videoUrl?: string,
  videoDuration?: number
): void {
  trackEvent("video_play", {
    video_title: videoTitle,
    video_url: videoUrl,
    video_duration: videoDuration,
    page_path: window.location.pathname,
  });
}

/**
 * Track video progress (25%, 50%, 75%, 90%)
 */
export function trackVideoProgress(
  videoTitle: string,
  progressPercent: number
): void {
  const milestones = [25, 50, 75, 90];
  const milestone = milestones.find(m => progressPercent >= m && progressPercent < m + 5);
  
  if (milestone) {
    const key = `video_${videoTitle}_${milestone}`;
    if (!window.videoProgressTracked) {
      window.videoProgressTracked = {};
    }
    if (!window.videoProgressTracked[key]) {
      window.videoProgressTracked[key] = true;
      trackEvent("video_progress", {
        video_title: videoTitle,
        progress_percent: milestone,
        page_path: window.location.pathname,
      });
    }
  }
}

/**
 * Enhanced gallery view tracking with more context
 */
export function trackGalleryViewEnhanced(
  galleryId: string,
  galleryTitle: string,
  imageCount?: number,
  category?: string
): void {
  trackEvent("gallery_view", {
    gallery_id: galleryId,
    gallery_title: galleryTitle,
    image_count: imageCount,
    gallery_category: category,
    page_path: window.location.pathname,
  });
}

/**
 * Enhanced image click tracking with more context
 */
export function trackImageClickEnhanced(
  imageId: string,
  galleryId?: string,
  imageIndex?: number,
  imageTitle?: string
): void {
  trackEvent("image_click", {
    image_id: imageId,
    image_title: imageTitle,
    gallery_id: galleryId,
    image_index: imageIndex,
    page_path: window.location.pathname,
  });
}
